import { useCallback, useEffect, useState } from "react";

import { VoltApiError } from "../voltApi";
import { useWorkshopAccess } from "../workshop/useWorkshopAccess";
import { errorText } from "../workshop/workshopApi";
import { fundingListApi } from "./fundingListApi";
import type { FundingListResponse, FundingRow, Scale } from "./types";

export const NO_ACCESS =
  "Der Quartiersfonds ist nur für die BV-Mitglieder der Fraktion. Deinem Konto fehlt die BV-Rolle.";

/** Loads one year of the list (latest without a year); `denied` carries the reason for a 403 or a missing role. */
export const useFundingList = (year?: number) => {
  const access = useWorkshopAccess();
  const [data, setData] = useState<FundingListResponse | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fundingListApi.list(year));
    } catch (e) {
      if (e instanceof VoltApiError && e.status === 403) setDenied(e.message);
      else setError(errorText(e));
    }
  }, [year]);

  useEffect(() => {
    if (access === "granted") void load();
  }, [access, load]);

  const update = useCallback(
    (id: number, f: (r: FundingRow) => FundingRow) =>
      setData((d) =>
        d
          ? {
              ...d,
              zeilen: d.zeilen.map((r) => (r.antragId === id ? f(r) : r)),
            }
          : d,
      ),
    [],
  );

  return {
    data,
    error,
    denied: access === "denied" ? NO_ACCESS : denied,
    reload: load,
    update,
  };
};

/** The scale text comes after login only; it does not change with saves. */
export const useFundingScale = (enabled: boolean) => {
  const [scale, setScale] = useState<Scale | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    fundingListApi.scale().then(
      (s) => live && setScale(s),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [enabled]);
  return scale;
};
